// 文件: components/XAiChatbot/index.tsx

import React, { useRef, useState, useMemo } from "react";
import { Tooltip, GetProp, Flex } from "antd";
import { Bubble, Prompts, FileCard } from "@ant-design/x";
import {
  UserOutlined,
  RedoOutlined,
  CopyOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import clsx from "clsx";
import MarkdownIt from "markdown-it";
import reactHtmlParser from "react-html-parser";
import type { Attachment } from "@/types/antdx";
import MarkdownGlobalStyle from "@/styles/markdown";
import { MessageStatus, MessageRole, Messages } from "@/types/XAiMessage";
import { XAiChatbotProps, ActionItem } from "@/types/XAiChatbot";
import emptyIcon from "@/assets/empty.png";
import arrowUp from "@/assets/arrow-up.png";
import arrowDown from "@/assets/arrow-down.png";
import thinkIcon from "@/assets/think.png";
import groupIcon from "@/assets/group.png";
import arrowDownBlue from "@/assets/arrow-down-blue.png";
import { useChatbotContext } from "@/hooks/useProviderContext";
import { useStyles, GlobalStyle } from "./styles";
import XAiSender from "../XAiSender";
import type { SendContent } from "@/types";

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
});

// 自定义 link_open 渲染规则
const defaultRender =
  md.renderer.rules.link_open ||
  ((tokens, idx, options, env, self) => {
    return self.renderToken(tokens, idx, options);
  });

// a 标签打开规则
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  // 添加 target="_blank"
  const aIndex = tokens[idx].attrIndex("target");
  if (aIndex < 0) {
    tokens[idx].attrPush(["target", "_blank"]);
  } else {
    tokens[idx].attrs![aIndex][1] = "_blank";
  }
  // 添加 rel="noopener noreferrer"
  const relIndex = tokens[idx].attrIndex("rel");
  if (relIndex < 0) {
    tokens[idx].attrPush(["rel", "noopener noreferrer"]);
  } else {
    tokens[idx].attrs![relIndex][1] = "noopener noreferrer";
  }
  return defaultRender(tokens, idx, options, env, self);
};

export interface ActionHeaderProps {
  execute: any[];
  thinks: string;
}

// ActionHeader 组件
export const ActionHeader: React.FC<ActionHeaderProps> = ({
  execute = [],
  thinks = "",
}) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(true);
  const [executeExpanded, setExecuteExpanded] = useState(false);

  // 关键修复：只要有思考内容或执行过程就显示，不要同时要求两者都有
  if (!thinks && (!execute || execute.length === 0)) return null;

  const last = execute[execute.length - 1] || {};
  const { name } = last;
  const icon = last?.icon || last?.extra?.icon;

  return (
    <div className={styles.actionHeaderWrapper}>
      {!expanded && (
        <div
          className={styles.actionTitle}
          onClick={() => setExpanded((v) => !v)}
        >
          <img src={thinkIcon} alt="icon" className={styles.actionHeaderIcon} />
          <span className={styles.flex1}>运行过程</span>
          <img alt="展开icon" src={arrowDown} className={styles.w("16px")} />
        </div>
      )}
      {expanded && (
        <div className={styles.actionHeaderDetail}>
          <div
            className={styles.actionDetailTitle}
            onClick={() => setExpanded((v) => !v)}
          >
            <img src={thinkIcon} alt="" className={clsx(styles.w(14))} />
            <div className={clsx(styles.flex1, styles.pl(10))}>
              隐藏运行过程
            </div>
            <img alt="收起icon" src={arrowUp} className={styles.w("16px")} />
          </div>

          {thinks && (
            <div
              className={styles.actionDetailContent}
              style={{ whiteSpace: "pre-line" }}
            >
              {thinks}
            </div>
          )}

          {execute && execute.length > 0 && (
            <>
              {!executeExpanded && (
                <div
                  className={styles.executeHiddenWrapper}
                  onClick={() => setExecuteExpanded((v) => !v)}
                >
                  {icon && (
                    <img src={icon} alt="icon" className={styles.h(15)} />
                  )}
                  <span className={styles.flex1}>{name}</span>
                  <img
                    alt="工具icon"
                    src={arrowDownBlue}
                    className={styles.w("16px")}
                  />
                </div>
              )}
              {executeExpanded && (
                <div className={styles.executeWrapper}>
                  <div
                    className={styles.executeTitle}
                    onClick={() => setExecuteExpanded((v) => !v)}
                  >
                    <img
                      src={groupIcon}
                      alt="icon"
                      className={styles.executeHeaderIcon}
                    />
                    <span className={styles.flex1}>隐藏运行详情</span>
                    <img
                      alt="展开详情icon"
                      src={arrowUp}
                      className={styles.w("16px")}
                    />
                  </div>
                  <div className={styles.executeContent}>
                    {execute.map((action: any, idx: number) => {
                      const thinkIcon2 =
                        action?.expandIcon ||
                        action?.extra?.expandIcon ||
                        action?.extra?.icon ||
                        action?.icon;
                      const thinkCost = action?.cost || action?.extra?.cost;
                      return (
                        <div
                          key={action.uniqueId || idx}
                          className={styles.actionHeaderDetailItem}
                        >
                          {thinkIcon2 && (
                            <img
                              src={thinkIcon2}
                              alt="icon"
                              className={styles.actionHeaderIcon}
                            />
                          )}
                          <span>{action?.name}</span>
                          <span className={styles.actionHeaderCost}>
                            {thinkCost ? `${thinkCost}s` : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// 默认消息功能区
export const defaultActions: ActionItem[] = [
  {
    key: "redo",
    icon: <RedoOutlined />,
    tooltip: "重新生成",
  },
  {
    key: "copy",
    icon: <CopyOutlined />,
    tooltip: "复制",
  },
  {
    key: "delete",
    icon: <DeleteOutlined />,
    tooltip: "删除",
  },
];

const XAiChatbot: React.FC<XAiChatbotProps> = (props) => {
  const styles = useStyles();
  // 使用新的 Hook 来处理 Provider 上下文
  const { mergedProps } = useChatbotContext(props);

  const {
    navbarShow = false,
    navbar,
    renderNavbar = null,
    clearBtnShow = true,
    inputShow = true,
    // renderMessageContent,
    // quickReplies = [],
    // quickRepliesVisible = true,
    // onQuickReplyClick,
    loading: propLoading = false,
    messageTooltip,
    avatar,
    userAvatar,
    messages: propMessages,
    text = "",
    footerTips = "",
    empty = null,
    emptyStateImage = emptyIcon,
    emptyStateText = "我的智能体",
    messageActions = defaultActions,
    onMessagesActionsCallback,
    // 点击帮助消息
    onSuggestMessageClick,
    onSend,
    onClear,
    onStop,
    providerId,
    // 消息顶部
    messageTop,
    enableUpload,
    uploadRequest,
  } = mergedProps as XAiChatbotProps;

  // 如果在 Provider 中，使用 Provider 的状态
  const messages = mergedProps.messages || propMessages || [];
  const loading = mergedProps.loading || propLoading;

  const [content, setContent] = useState<string>(text); // 输入框文本
  const [files, setFiles] = useState<Attachment[]>([]);
  const lastMessageId = useRef<string>("");
  const isScriptScrolling = useRef<boolean>(false);
  const chatId = providerId
    ? `za-chatbot-container-${providerId}`
    : "za-chatbot-container";

  // 滚动到最底部
  const scrollToBottom = () => {
    isScriptScrolling.current = true;
    setTimeout(() => {
      const mainChatWrapper = document.getElementById(chatId);
      if (mainChatWrapper) {
        mainChatWrapper.scrollTop = mainChatWrapper?.scrollHeight;
      }
      isScriptScrolling.current = false;
    }, 0);
  };

  // 消息功能区组件
  const FooterActions: React.FC<{ data: Messages; lastMessage: boolean }> = ({
    data,
    lastMessage = false,
  }) => (
    <div className={clsx(styles.flex, styles.gap(13))}>
      {messageActions.map((action: ActionItem, index: number) => {
        if (!lastMessage && action.key === "redo") return null;
        return (
          <Tooltip key={action.key} title={action.tooltip}>
            <span
              className={styles.cursor("pointer")}
              onClick={() => onMessagesActionsCallback?.(index, data)}
            >
              {action.icon}
              {action.render?.(index, data)}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );

  // 消息底部区域
  const MessageFooter: React.FC<{ data: Messages; lastMessage: boolean }> = ({
    data,
    lastMessage = false,
  }) => (
    <>
      {messageTooltip?.(data)}
      <FooterActions data={data} lastMessage={lastMessage} />
    </>
  );

  // 聊天角色
  const rolesObject: GetProp<typeof Bubble.List, "role"> = {
    assistant: {
      placement: "start",
      avatar: () => {
        return (
          avatar || (
            <UserOutlined
              className={clsx(styles.bg("#fde3cf"), styles.userAvatar)}
            />
          )
        );
      },
      typing: { step: 5, interval: 20, effect: "typing" },
      style: {
        maxWidth: 600,
      },
      classNames: {
        content: "assistant-content",
      },
    },
    user: {
      placement: "end",
      avatar: () => {
        return (
          userAvatar || (
            <UserOutlined
              className={clsx(styles.bg("#87d068"), styles.userAvatar)}
            />
          )
        );
      },
      classNames: {
        content: "user-content",
      },
    },
    suggestion: {
      placement: "start",
      avatar: () => {
        return (
          avatar || (
            <UserOutlined
              className={clsx(styles.bg("#fde3cf"), styles.userAvatar)}
            />
          )
        );
      },
      variant: "borderless",
    },
  };

  // 触发发送
  const handleSend = (obj: SendContent) => {
    // 直接使用合并后的 onSend，自动处理 Provider 和独立模式
    onSend?.(obj);
    setContent(""); // 发送后清空输入框内容
    setFiles([]); // 发送后清空文件
    scrollToBottom();
  };

  // 输出内容
  const handleChange = (str: string) => {
    setContent(str);
  };

  // 停止生成
  const handleStop = () => {
    onStop?.();
  };

  // 导航栏
  const NavBar: React.FC = () => {
    if (renderNavbar) {
      return renderNavbar();
    }
    if (navbar?.title) {
      return (
        <header className={styles.navbar}>
          {navbar.avatar && (
            <img src={navbar.avatar} className={styles.avatar} alt="" />
          )}
          <div>
            <div className={styles.title}>{navbar.title}</div>
            <div className={styles.subtitle}>{navbar.subtitle}</div>
          </div>
        </header>
      );
    }
    return <></>;
  };

  // 确认清除
  const confirmClear = () => {
    onClear?.();
  };

  // 空状态
  const EmptyState = useMemo(
    () => () =>
      empty || (
        <div className={styles.emptyWrapper}>
          <img
            src={emptyStateImage}
            alt="空状态图标"
            className={styles.emptyImg}
          />
          <div
            className={clsx(
              styles.text(16),
              styles.weight(600),
              styles.textColor("#343434"),
            )}
          >
            {emptyStateText}
          </div>
        </div>
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    [emptyStateImage, emptyStateText],
  );

  return (
    <>
      <GlobalStyle />
      <MarkdownGlobalStyle />
      <div id="x-ai-chatbot" className={styles.wrapper}>
        {navbarShow && <NavBar />}
        {/** 消息容器 */}
        <div className={styles.messageContainer}>
          {/* 消息列表主体 */}
          {messages?.length ? (
            <Bubble.List
              id={chatId}
              role={rolesObject}
              className={styles.messageList}
              items={messages.map((msg: any) => {
                const {
                  id,
                  role,
                  status,
                  type,
                  execute = [],
                  thinks = "",
                  extra = { noFooter: false },
                  stepContent,
                } = msg as Messages;
                // 最后一条 AI消息标识
                if (role === MessageRole.assistant) {
                  lastMessageId.current = id;
                }
                // 会话内容
                const bubbleContent: any = {
                  key: id,
                  role,
                  loading: status === MessageStatus.init,
                  header: () =>
                    messageTop || (
                      <ActionHeader execute={execute} thinks={thinks} />
                    ),
                  content: (() => {
                    // 文本消息
                    if (type === "TextMessage") {
                      // 用户消息
                      if (role === "user") {
                        return (
                          <div
                            className="ai-markdown-body"
                            style={{
                              minWidth: 0,
                              maxWidth: 600,
                            }}
                          >
                            {msg.content?.text}
                          </div>
                        );
                      }

                      // 输出html字符串
                      const html = md.render(msg.content?.text || "");
                      // 生成React节点
                      const htmlString = reactHtmlParser(html);
                      return (
                        <>
                          {/* 输出阶段性回答内容 */}
                          {stepContent?.length &&
                            stepContent.map((item) => {
                              // 输出html字符串
                              const html2 = md.render(item.content || "");
                              // 生成React节点
                              const stepHtmlString = reactHtmlParser(html2);
                              return (
                                <div
                                  key={item.id}
                                  className="ai-markdown-body step-content"
                                  style={{
                                    minWidth: 0,
                                    maxWidth: 600,
                                  }}
                                >
                                  <div>{stepHtmlString}</div>
                                  <div className={styles.stepLine} />
                                </div>
                              );
                            })}
                          {/** 输出最终回答内容 */}
                          <div
                            className="ai-markdown-body"
                            style={{
                              minWidth: 0,
                              maxWidth: 600,
                            }}
                          >
                            {htmlString}
                          </div>
                        </>
                      );
                    }
                    // 提示类型
                    if (type === "SuggestionMessage") {
                      return (
                        <Prompts
                          vertical
                          items={msg.content as any}
                          onItemClick={(info) => {
                            onSuggestMessageClick?.(info?.data, id);
                            if (info.data.description) {
                              onSend?.({
                                text: info.data.description as string,
                              });
                            }
                          }}
                        />
                      );
                    }
                    // 其他类型...
                    return null;
                  })(),
                };

                if (!extra.noFooter) {
                  bubbleContent.footer = () => {
                    const msgFiles = msg.content?.files || [];
                    if (role === MessageRole.user && msgFiles.length) {
                      return (
                        <Flex gap={8} wrap justify="flex-end">
                          {msgFiles.map((f: any) => (
                            <FileCard
                              styles={{
                                file:
                                  f?.fileType === "image"
                                    ? {
                                        width: 100,
                                        height: 100,
                                      }
                                    : {},
                              }}
                              key={f.fileId}
                              {...f}
                              name={f.fileName}
                              size={f.fileSize}
                              type={f.fileType}
                              src={f.fileUrl}
                            />
                          ))}
                        </Flex>
                      );
                    }
                    if (
                      role === MessageRole.assistant &&
                      status !== MessageStatus.failed
                    ) {
                      return (
                        <MessageFooter
                          data={msg as Messages}
                          lastMessage={lastMessageId.current === id}
                        />
                      );
                    }
                  };
                }

                return bubbleContent;
              })}
            />
          ) : (
            <EmptyState />
          )}
        </div>
        {/* 输入框 */}
        {inputShow && (
          <XAiSender
            value={content}
            loading={loading}
            footerTips={footerTips}
            clearBtnShow={clearBtnShow}
            enableUpload={enableUpload}
            files={files}
            onChangeFiles={setFiles}
            uploadRequest={uploadRequest}
            onChange={handleChange}
            onSubmit={handleSend}
            onStop={handleStop}
            onClear={confirmClear}
          />
        )}
      </div>
    </>
  );
};

export default XAiChatbot;
